import {
    FaAddressBook, FaPhoneAlt, FaEnvelope, FaBuilding, FaMapMarkerAlt, FaGlobe,
    FaBirthdayCake, FaIdCard, FaLock, FaWhatsapp, FaTag, FaCalendarAlt,
} from "react-icons/fa";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { Badge } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";

const text = (value, fallback = "—") => String(value || "").trim() || fallback;
const phoneForUrl = (value = "") => String(value || "").replace(/[^0-9+]/g, "");
const whatsappUrl = (value = "") => `https://wa.me/${String(value || "").replace(/\D/g, "")}`;

const addressLine = (address = {}) => [
    [address.street, address.number].filter(Boolean).join(" "),
    address.floor,
    [address.city, address.state].filter(Boolean).join(", "),
    address.postalCode,
    address.country,
].filter(Boolean).join(" · ");

const initials = (name = "") => String(name || "C").trim().split(/\s+/).filter(Boolean).slice(0, 2)
    .map((part) => part[0]).join("").toUpperCase() || "C";

const DetailRow = ({ label, value, icon = null, children = null }) => (
    <div className="LStudio__contacts_detail_row">
        <span className="LStudio__contacts_detail_label">{icon}{label}</span>
        <div className="LStudio__contacts_detail_value">{children ?? text(value)}</div>
    </div>
);

export const ContactPreview = ({ contact = null, group = null, showSecurity = true }) => {
    if (!contact) return null;

    const phones = Array.isArray(contact.phones) ? contact.phones : [];
    const emails = Array.isArray(contact.emails) ? contact.emails : [];
    const addresses = Array.isArray(contact.addresses) ? contact.addresses : [];
    const socials = Array.isArray(contact.socials) ? contact.socials : [];
    const importantDates = Array.isArray(contact.importantDates) ? contact.importantDates : [];
    const customFields = Array.isArray(contact.customFields) ? contact.customFields : [];
    const tags = Array.isArray(contact.tags) ? contact.tags : [];

    return <div className="LStudio__contacts_preview">
        {showSecurity && <div className="LStudio__contacts_detail_security">
            <FaLock />
            <span>AES-256-GCM en reposo · sólo se descifra para tu usuario autenticado.</span>
        </div>}

        <div className="LStudio__contacts_detail_hero">
            <div className="LStudio__contacts_avatar LStudio__contacts_avatar--large">
                {(contact.photoDataUrl || contact.photoUrl)
                    ? <img src={contact.photoDataUrl || contact.photoUrl} alt="" />
                    : initials(contact.displayName)}
            </div>
            <div>
                <h3>{contact.displayName || "Contacto"}</h3>
                <div className="LStudio__contacts_badges">
                    {group && <Badge tone="neutral" size="sm">{group.name}</Badge>}
                    {!group && <Badge tone="neutral" size="sm">Sin grupo</Badge>}
                    <Badge tone="neutral" size="sm">{contact.contactType || "PERSONAL"}</Badge>
                    {contact.relationship && <Badge tone="neutral" size="sm">{contact.relationship}</Badge>}
                    {contact.isFavorite && <Badge tone="accent" size="sm">Destacado</Badge>}
                </div>
            </div>
        </div>

        <div className="LStudio__contacts_detail_grid">
            <section className="LStudio__contacts_detail_section">
                <h4><FaAddressBook /> Identidad</h4>
                <DetailRow label="Nombre" value={contact.firstName} />
                <DetailRow label="Segundo nombre" value={contact.middleName} />
                <DetailRow label="Apellido" value={contact.lastName} />
                <DetailRow label="Apodo" value={contact.nickname} />
                <DetailRow label="Relación" value={contact.relationship} />
                <DetailRow label="Idioma" value={contact.language} />
                <DetailRow label="Zona horaria" value={contact.timezone} />
            </section>

            <section className="LStudio__contacts_detail_section">
                <h4><FaBuilding /> Trabajo</h4>
                <DetailRow label="Organización" value={contact.organization} />
                <DetailRow label="Cargo" value={contact.jobTitle} />
                <DetailRow label="Área" value={contact.department} />
                <DetailRow label="Origen" value={contact.source} />
                <DetailRow label="Cómo se conocieron" value={contact.howWeMet} />
                <DetailRow label="Medio preferido" value={contact.preferredContactMethod} />
            </section>
        </div>

        {phones.length > 0 && <section className="LStudio__contacts_detail_section LStudio__contacts_detail_section--wide">
            <h4><FaPhoneAlt /> Teléfonos</h4>
            <div className="LStudio__contacts_channel_list">
                {phones.map((phone, index) => <div key={`${phone.number}_${index}`} className="LStudio__contacts_channel_item">
                    <div>
                        <strong>{phone.label || "Teléfono"}{phone.isPrimary ? " · Principal" : ""}</strong>
                        <span>{phone.number}{phone.extension ? ` · Int. ${phone.extension}` : ""}</span>
                        <small>{[phone.whatsapp && "WhatsApp", phone.telegram && "Telegram", phone.signal && "Signal"].filter(Boolean).join(" · ")}</small>
                    </div>
                    <div className="LStudio__contacts_channel_actions">
                        <AppButton size="sm" variant="soft" tone="neutral" icon={<FaPhoneAlt />}
                            href={`tel:${phoneForUrl(phone.number)}`} target="_self">Llamar</AppButton>
                        {phone.whatsapp && <AppButton size="sm" variant="soft" tone="neutral" icon={<FaWhatsapp />}
                            href={whatsappUrl(phone.number)}>WhatsApp</AppButton>}
                    </div>
                </div>)}
            </div>
        </section>}

        {emails.length > 0 && <section className="LStudio__contacts_detail_section LStudio__contacts_detail_section--wide">
            <h4><FaEnvelope /> Correos</h4>
            <div className="LStudio__contacts_channel_list">
                {emails.map((email, index) => <div key={`${email.email}_${index}`} className="LStudio__contacts_channel_item">
                    <div><strong>{email.label || "Correo"}{email.isPrimary ? " · Principal" : ""}</strong><span>{email.email}</span></div>
                    <AppButton size="sm" variant="soft" tone="neutral" icon={<FaEnvelope />}
                        href={`mailto:${email.email}`} target="_self">Escribir</AppButton>
                </div>)}
            </div>
        </section>}

        {addresses.length > 0 && <section className="LStudio__contacts_detail_section LStudio__contacts_detail_section--wide">
            <h4><FaMapMarkerAlt /> Direcciones</h4>
            {addresses.map((address, index) => <DetailRow key={index} label={address.label || "Dirección"} value={addressLine(address)} />)}
        </section>}

        <div className="LStudio__contacts_detail_grid">
            <section className="LStudio__contacts_detail_section">
                <h4><FaBirthdayCake /> Fechas</h4>
                <DetailRow label="Cumpleaños" value={contact.birthday} />
                <DetailRow label="Aniversario" value={contact.anniversary} />
                {importantDates.map((date, index) => <DetailRow key={index} label={date.label || "Fecha"} value={date.date} icon={<FaCalendarAlt />} />)}
            </section>

            <section className="LStudio__contacts_detail_section">
                <h4><FaIdCard /> Identificación</h4>
                <DetailRow label="Documento" value={[contact.documentType, contact.documentNumber].filter(Boolean).join(" ")} />
                <DetailRow label="CUIT / CUIL / fiscal" value={contact.taxId} />
                <DetailRow label="Sitio web" icon={<FaGlobe />}>
                    {contact.website ? <a href={contact.website} target="_blank" rel="noreferrer">{contact.website}</a> : "—"}
                </DetailRow>
            </section>
        </div>

        {socials.length > 0 && <section className="LStudio__contacts_detail_section LStudio__contacts_detail_section--wide">
            <h4><FaGlobe /> Redes y perfiles</h4>
            {socials.map((social, index) => <DetailRow key={index} label={social.platform || "Perfil"}>
                {social.url ? <a href={social.url} target="_blank" rel="noreferrer">{social.username || social.url}</a> : text(social.username)}
            </DetailRow>)}
        </section>}

        {tags.length > 0 && <section className="LStudio__contacts_detail_section LStudio__contacts_detail_section--wide">
            <h4><FaTag /> Etiquetas</h4>
            <div className="LStudio__contacts_detail_tags">{tags.map((tag) => <Badge key={tag} tone="neutral" size="sm">{tag}</Badge>)}</div>
        </section>}

        {(contact.notes || contact.privateNotes) && <section className="LStudio__contacts_detail_section LStudio__contacts_detail_section--wide">
            <h4><FaLock /> Notas</h4>
            {contact.notes && <DetailRow label="Generales" value={contact.notes} />}
            {contact.privateNotes && <DetailRow label="Privadas" value={contact.privateNotes} />}
        </section>}

        {customFields.length > 0 && <section className="LStudio__contacts_detail_section LStudio__contacts_detail_section--wide">
            <h4>Campos personalizados</h4>
            {customFields.map((field, index) => <DetailRow key={index} label={field.label || `Campo ${index + 1}`} value={field.value} />)}
        </section>}
    </div>;
};
